import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { favoritesService } from '../api';
import type { Favorite } from '../api/services/favoritesService';
import {
  formatPrice,
  formatMileage,
  getFuelTypeDisplay,
} from '../mysearch/mysearchConstants';
import { getFeatureLabels } from '../utils/featureMapper';
import '../overview/overview.css';

export default function Favorites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchFavorites = async (page = 1) => {
    try {
      setLoading(true);
      const response = await favoritesService.getFavorites(
        page,
        pagination.limit
      );
      setFavorites(response.favorites);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleRemoveFavorite = async (
    listingId: string,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (removingId === listingId) return;

    setRemovingId(listingId);
    try {
      await favoritesService.removeFavorite(listingId);
      setFavorites(prev => prev.filter(fav => fav.listingId !== listingId));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (error: any) {
      console.error('Error removing favorite:', error);
      alert(error.message || t('favorites.failed_remove'));
    } finally {
      setRemovingId(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchFavorites(newPage);
    }
  };

  return (
    <div className="dashboard-favorites" style={{ paddingBottom: 40 }}>
      {/* Content */}
      <div className="dash-panel shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">
            {pagination.total > 0
              ? t(pagination.total === 1 ? 'favorites.count_one' : 'favorites.count_other', { count: pagination.total })
              : t('favorites.none_yet')}
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
            <p className="mt-3">{t('favorites.loading')}</p>
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-5">
            <i
              className="fa-regular fa-heart"
              style={{ fontSize: '4rem', color: '#ccc', marginBottom: '1rem' }}
            ></i>
            <h4>{t('favorites.no_favorites')}</h4>
            <p className="text-muted">
              {t('favorites.no_favorites_desc')}
            </p>
            <Link to="/mysearch" className="btn btn-custom mt-3">
              <i className="fa-solid fa-search me-2"></i> {t('favorites.browse')}
            </Link>
          </div>
        ) : (
          <>
            <div className="row g-4">
              {favorites.map(favorite => {
                const listing = favorite.listing || {};
                const primaryPhoto =
                  listing.thumbnailUrl ||
                  listing.image ||
                  listing.images?.[0] ||
                  '/images/placeholder.jpg';
                const isTop =
                  listing.promotionLevel !== 'STANDARD' || listing.featured;
                const showGoodPrice =
                  listing.originalPrice &&
                  parseFloat(listing.price) < parseFloat(listing.originalPrice);

                return (
                  <div key={favorite.id} className="col-12 col-md-6 col-lg-4">
                    <div className="dash-panel p-3 h-100 d-flex flex-column border-0 shadow-sm" style={{ transition: 'transform 0.2s, box-shadow 0.2s', borderRadius: '12px' }}>
                      <div className="position-relative mb-3">
                        {isTop && (
                          <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-2 px-2 py-1 fw-bold border" style={{ zIndex: 2, fontSize: '10px' }}>
                            TOP RATED
                          </span>
                        )}
                        <img
                          src={primaryPhoto}
                          className="img-fluid"
                          alt={listing.title || t('common.car')}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            cursor: 'pointer',
                            borderRadius: '8px'
                          }}
                          onClick={() => navigate(`/listing/${listing.slug || listing.id || favorite.listingId}`)}
                        />
                      </div>
                      <div className="flex-grow-1 d-flex flex-column">
                        <h6 className="card-title mb-1 fw-bold text-truncate" style={{ fontSize: '15px' }} onClick={() => navigate(`/listing/${listing.slug || listing.id || favorite.listingId}`)} role="button">
                          {listing.year || ''} {listing.make || ''} {listing.model || ''}
                        </h6>
                        <h5 className="text-success fw-bold mb-2" style={{ fontSize: '16px' }}>
                          {formatPrice(listing.price || 0)}
                          {showGoodPrice && (
                            <small className="text-muted ms-2 bg-success bg-opacity-10 text-success px-2 py-0 rounded" style={{ fontSize: '10px' }}>
                              <i className="bi bi-tag-fill me-1"></i> {t('favorites.good_price')}
                            </small>
                          )}
                        </h5>
                        <p className="small text-muted mb-3 d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                          <span className="text-truncate">
                            <i className="bi bi-speedometer2 me-1"></i> {formatMileage(listing.mileage || 0)} km
                          </span>
                          <span className="text-truncate">
                            <i className="bi bi-ev-station me-1"></i> {getFuelTypeDisplay(listing.fuelType || '')}
                          </span>
                        </p>
                        
                        <div className="d-flex gap-2 mt-auto pt-2 border-top border-light">
                          <Link
                            to={`/listing/${listing.slug || listing.id || favorite.listingId}`}
                            className="btn btn-outline-primary btn-sm flex-grow-1 fw-semibold"
                          >
                            <i className="fa-regular fa-envelope me-1"></i> View
                          </Link>
                          <button
                            className="btn btn-sm flex-shrink-0"
                            onClick={e => handleRemoveFavorite(favorite.listingId, e)}
                            disabled={removingId === favorite.listingId}
                            style={{
                              color: '#ff4757',
                              background: '#ffe4e6',
                              border: 'none'
                            }}
                          >
                            {removingId === favorite.listingId ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <i className="fa-solid fa-heart"></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <nav aria-label="Favorites pagination" className="mt-4">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}
                  >
                    <a
                      className="page-link"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        handlePageChange(pagination.page - 1);
                      }}
                    >
                      {t('favorites.previous')}
                    </a>
                  </li>
                  {Array.from(
                    { length: Math.min(pagination.totalPages, 5) },
                    (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <li
                          key={pageNum}
                          className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              handlePageChange(pageNum);
                            }}
                          >
                            {pageNum}
                          </a>
                        </li>
                      );
                    }
                  )}
                  {pagination.totalPages > 5 && (
                    <>
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                      <li className="page-item">
                        <a
                          className="page-link"
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            handlePageChange(pagination.totalPages);
                          }}
                        >
                          {pagination.totalPages}
                        </a>
                      </li>
                    </>
                  )}
                  <li
                    className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}
                  >
                    <a
                      className="page-link"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        handlePageChange(pagination.page + 1);
                      }}
                    >
                      {t('favorites.next')}
                    </a>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
}
